import { Injectable } from '@angular/core';
import { CapacitorHttp } from '@capacitor/core';
// Import the plugin instance the package registers itself — it carries the web
// implementation (browser Geolocation API). Re-registering it here with
// registerPlugin('BackgroundGeolocation') would drop that fallback and throw
// UNIMPLEMENTED on web.
import {
  BackgroundGeolocation,
  Location,
  CallbackError,
} from '@capgo/background-geolocation';
import { Geolocation } from '@capacitor/geolocation';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorage } from 'src/app/class/local-storage';
import { CONSTANTS } from 'src/app/config/constants';
import { Observable, Subject } from 'rxjs';
import { IGeolocationEvent } from 'src/app/interface/geolocation-event';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth/auth.service';
import { GeofenceNotificationsService } from '../geofence-notifications/geofence-notifications.service';

/**
 * Send cadence, in milliseconds. The client sends a fix every 5s while a site
 * performance (work commitment) is in progress, and every 30s otherwise. This
 * is time-based, not distance-based: we want a parked-vehicle heartbeat, so a
 * stationary crew keeps reporting. Passed to the native layer as
 * `minIntervalMs`, which throttles the native update/POST cadence; the same
 * interval also gates our own submit as a cross-platform safety net (see below).
 */
export const GEO_INTERVAL_ACTIVE_MS = 5000;
export const GEO_INTERVAL_IDLE_MS = 30000;

@Injectable({
  providedIn: 'root',
})
export class GeolocationService {
  private subject: Subject<IResponse> = new Subject();
  private latestLocation: { lat: number; long: number } | null = null;

  // The Capgo plugin is a single start/stop session (not the multi-watcher model
  // of the old community plugin), and start() rejects with ALREADY_STARTED if
  // called twice. We track the running interval so we only restart the native
  // session when the cadence actually changes, and serialise start/stop through
  // `transition` so overlapping callers (site-performance and shift updates both
  // fire startTracking) can never double-start.
  private currentIntervalMs: number | null = null;
  private transition: Promise<void> = Promise.resolve();

  // The native `minIntervalMs` throttles native POSTs and the Android update
  // interval, but on iOS the JS callback can still fire more often than the
  // interval. We submit from the callback (our ingest expects `{ locations: [] }`,
  // not Capgo's native POST body), so we gate the submit by time ourselves to
  // guarantee the 5s/30s cadence on both platforms.
  private lastSubmittedAt = 0;

  constructor(
    private translateService: TranslateService,
    private authService: AuthService,
    private geofenceNotificationsService: GeofenceNotificationsService
  ) {}

  public async submit(data: IGeolocationEvent) {
    const company = this.authService.company;

    if (!company) {
      return;
    }
    let token = this.authService.token;
    if (!token) {
      return;
    }

    // Extract location
    const location = data.locations?.[0];
    if (!location?.lat || !location?.long) {
      return;
    }

    token = 'Bearer ' + token;
    // Local-only override: without a gateway in front, geolocation/bulk goes straight to a
    // locally-running geolocation-api (see environment.v4.ts). Absent everywhere else.
    const geoBase = (environment as { apiUrl: string; geolocationUrl?: string }).geolocationUrl ?? environment.apiUrl;
    let url = geoBase.replace('{language}', 'en');
    url += '/';
    url += company.id;
    url += '/geolocation/bulk';
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: token,
    };

    if (!environment.production) {
      console.log(
        `GeolocationService sending geolocation → ${url}`,
        { lat: location.lat, long: location.long, at: new Date().toISOString() }
      );
    }

    CapacitorHttp.post({
      url,
      headers,
      data,
    }).then(
      (response) => {
        if (!environment.production) {
          console.log('GeolocationService submit', JSON.stringify(response));
        }
      },
      (error) => {
        if (!environment.production) {
          console.error('GeolocationService submit', JSON.stringify(error));
        }
      }
    );
  }

  /**
   * Start (or re-tune) background tracking at the given send cadence.
   *
   * @param intervalMs how often to report a fix. Defaults to the idle cadence;
   *   callers pass {@link GEO_INTERVAL_ACTIVE_MS} while a site is in progress.
   */
  public startTracking(intervalMs: number = GEO_INTERVAL_IDLE_MS) {
    // Serialise against any in-flight start/stop so we never double-start.
    this.transition = this.transition
      .catch(() => undefined)
      .then(async () => {
        if (this.currentIntervalMs === intervalMs) {
          return;
        }
        // Changing cadence: tear the native session down and bring it back up
        // with the new interval (minIntervalMs is fixed at start time).
        if (this.currentIntervalMs !== null) {
          await BackgroundGeolocation.stop().catch(() => undefined);
          this.currentIntervalMs = null;
        }
        await this.addWatcher(intervalMs);
      });
    return this.transition;
  }

  private async addWatcher(intervalMs: number) {
    this.lastSubmittedAt = 0;
    this.currentIntervalMs = intervalMs;
    try {
      await BackgroundGeolocation.start(
        {
          backgroundMessage: this.translateService.instant(
            'APP_NAME - Shift tracking',
            { APP_NAME: LocalStorage.get(CONSTANTS.localStorage.keys.appName) }
          ),
          backgroundTitle: this.translateService.instant('Location Tracking'),
          requestPermissions: true,
          stale: true,
          // Emit on every provider update. A non-zero distanceFilter suppresses
          // updates while the device is stationary, which would defeat the
          // parked-vehicle heartbeat that minIntervalMs gives us.
          distanceFilter: 0,
          // Native send cadence — at most one update/POST per interval.
          minIntervalMs: intervalMs,
        },
        (location?: Location, error?: CallbackError) => {
          if (error) {
            if (error.code === 'NOT_AUTHORIZED') {
              this.promptForPermission();
            } else if (!environment.production) {
              console.error('GeolocationService watcher', JSON.stringify(error));
            }
            return;
          }
          if (!location?.latitude) {
            return;
          }

          this.latestLocation = {
            lat: location.latitude,
            long: location.longitude,
          };

          // Geofence checks are local and cheap; run them on every fix so
          // enter/exit stays responsive regardless of the submit cadence.
          this.geofenceNotificationsService.checkLocation(
            location.latitude,
            location.longitude
          );

          // Gate the server submit to the configured cadence.
          const now = Date.now();
          if (now - this.lastSubmittedAt >= intervalMs) {
            this.lastSubmittedAt = now;
            this.submit({
              locations: [
                {
                  lat: location.latitude,
                  long: location.longitude,
                },
              ],
            });
          }

          this.subject?.next({
            latitude: location.latitude,
            longitude: location.longitude,
          });
        }
      );
    } catch (error) {
      // A concurrent caller may have won the race; treat the session as down so
      // a later startTracking can retry.
      this.currentIntervalMs = null;
      if (!environment.production) {
        console.error('GeolocationService start', JSON.stringify(error));
      }
    }
  }

  private promptForPermission() {
    if (
      window.confirm(
        this.translateService.instant(
          'APP_NAME needs your location, but does not have permission.',
          {
            APP_NAME: LocalStorage.get(CONSTANTS.localStorage.keys.appName),
          }
        ) +
          '\n\n' +
          this.translateService.instant('Open settings now?')
      )
    ) {
      BackgroundGeolocation.openSettings();
    }
  }

  public stopTracking() {
    this.transition = this.transition
      .catch(() => undefined)
      .then(async () => {
        if (this.currentIntervalMs === null) {
          return;
        }
        this.currentIntervalMs = null;
        await BackgroundGeolocation.stop().catch(() => undefined);
      });
    return this.transition;
  }

  public getCurrentGeolocation(
    dataToSubmit?: IGeolocationEvent
  ): Observable<IResponse> {
    const subject = new Subject<IResponse>();

    const emit = (latitude: number, longitude: number) => {
      this.latestLocation = { lat: latitude, long: longitude };
      subject.next({ latitude, longitude });
      subject.complete();

      if (dataToSubmit) {
        dataToSubmit.locations = [{ lat: latitude, long: longitude }];
        this.submit(dataToSubmit);
      }
    };

    // Prefer the freshest fix from the running tracker; otherwise take a
    // one-shot reading (e.g. weather at launch, before a shift is tracking).
    if (this.latestLocation) {
      emit(this.latestLocation.lat, this.latestLocation.long);
    } else {
      Geolocation.getCurrentPosition({ enableHighAccuracy: true }).then(
        (position) => emit(position.coords.latitude, position.coords.longitude),
        (error) => {
          if (!environment.production) {
            console.error(
              'GeolocationService getCurrentGeolocation',
              JSON.stringify(error)
            );
          }
          subject.complete();
        }
      );
    }

    return subject.asObservable();
  }

  public trackingListener(): Observable<IResponse> {
    this.subject = new Subject<IResponse>();
    return this.subject.asObservable();
  }

  public openSettings() {
    BackgroundGeolocation.openSettings();
  }
}

interface IResponse {
  latitude: number;
  longitude: number;
}
